/**
 * PriceHistoryChart — упрощённый график цены базового актива под payoff-графиком.
 * Назначение: «как цена вообще ходила», хаи и провалы за период. Не теханализ.
 * Пресеты: Всё / 5 лет (дефолт) / 1 год / 1 мес. Данные: /market/price-history (кеш на бэке).
 *
 * Интерактив: наведение — цена/дата в точке (клик — закрепить crosshair).
 * BE-коридор: если переданы beLow/beHigh (точки нуля из расчёта окупаемости),
 * рисуем тонкие пунктирные линии и лёгкую заливку коридора убытка — видно,
 * выходила ли цена за требуемый диапазон исторически.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api";

type Period = "max" | "5y" | "1y" | "1mo";
const PRESETS: { p: Period; label: string }[] = [
  { p: "max", label: "Всё" }, { p: "5y", label: "5 лет" }, { p: "1y", label: "1 год" }, { p: "1mo", label: "1 мес" },
];

const W = 640, H = 190, PAD_L = 8, PAD_R = 64, PAD_T = 18, PAD_B = 22;

const dFmt = (iso: string, long = false) => {
  const [y, m, d] = iso.split("-");
  return long ? `${d}.${m}.${y}` : `${m}.${y.slice(2)}`;
};
const pFmt = (v: number) => v >= 1000 ? Math.round(v).toLocaleString("ru") : v >= 100 ? v.toFixed(1) : v.toFixed(2);

export function PriceHistoryChart({ symbol, beLow, beHigh }: { symbol: string; beLow?: number | null; beHigh?: number | null }) {
  const [period, setPeriod] = useState<Period>("5y");
  const [data, setData] = useState<Record<string, api.PriceHistoryOut>>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hover, setHover] = useState<{ date: string; price: number } | null>(null);
  const [pinned, setPinned] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const symRef = useRef(symbol);

  // смена тикера → сбрасываем накопленные периоды и crosshair
  useEffect(() => {
    if (symRef.current !== symbol) { symRef.current = symbol; setData({}); setErr(null); setHover(null); setPinned(false); }
  }, [symbol]);

  const key = `${symbol}:${period}`;
  useEffect(() => {
    if (!symbol || data[key]) return;
    let dead = false;
    setLoading(true); setErr(null);
    api.fetchPriceHistory(symbol, period)
      .then((r) => { if (!dead) setData((m) => ({ ...m, [key]: r })); })
      .catch((e) => { if (!dead) setErr(e.message ?? String(e)); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
    // eslint-disable-next-line
  }, [symbol, period]);

  const d = data[key];
  const view = useMemo(() => {
    if (!d || d.points.length < 2) return null;
    const pts = d.points;
    const t0 = new Date(pts[0][0]).getTime(), t1 = new Date(pts[pts.length - 1][0]).getTime();
    let lo = d.low.price, hi = d.high.price;
    // BE-уровни должны попасть в кадр, даже если цена туда ещё не ходила
    if (beLow != null && beLow > 0) lo = Math.min(lo, beLow);
    if (beHigh != null && beHigh > 0) hi = Math.max(hi, beHigh);
    const padY = (hi - lo) * 0.08 || hi * 0.05;
    lo -= padY; hi += padY;
    const x = (iso: string) => PAD_L + ((new Date(iso).getTime() - t0) / Math.max(1, t1 - t0)) * (W - PAD_L - PAD_R);
    const y = (v: number) => PAD_T + (1 - (v - lo) / (hi - lo)) * (H - PAD_T - PAD_B);
    const path = pts.map(([dt, v], i) => `${i ? "L" : "M"}${x(dt).toFixed(1)},${y(v as number).toFixed(1)}`).join("");
    const area = `${path}L${x(pts[pts.length - 1][0]).toFixed(1)},${H - PAD_B}L${x(pts[0][0]).toFixed(1)},${H - PAD_B}Z`;
    const grid = [0.25, 0.5, 0.75].map((f) => ({ yy: PAD_T + f * (H - PAD_T - PAD_B), v: hi - f * (hi - lo) }));
    return { x, y, t0, t1, path, area, grid, first: pts[0][0], last: pts[pts.length - 1][0] };
  }, [d, beLow, beHigh]);

  // --- crosshair: ближайшая точка к позиции указателя ---
  function locate(e: React.PointerEvent<SVGSVGElement>): { date: string; price: number } | null {
    if (!view || !d || !svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const xPx = ((e.clientX - rect.left) / rect.width) * W;
    const t = view.t0 + ((xPx - PAD_L) / (W - PAD_L - PAD_R)) * (view.t1 - view.t0);
    let best = 0, bd = Infinity;
    d.points.forEach(([dt], i) => {
      const delta = Math.abs(new Date(dt).getTime() - t);
      if (delta < bd) { bd = delta; best = i; }
    });
    const [date, price] = d.points[best];
    return { date, price: price as number };
  }
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => { if (!pinned) setHover(locate(e)); };
  const onClick = (e: React.PointerEvent<SVGSVGElement>) => {
    if (pinned) { setPinned(false); setHover(locate(e)); }
    else { setHover(locate(e)); setPinned(true); }
  };
  const onLeave = () => { if (!pinned) setHover(null); };

  const chg = d ? ((d.last.price - (d.points[0][1] as number)) / (d.points[0][1] as number)) * 100 : null;
  const hasBE = beLow != null && beLow > 0 && beHigh != null && beHigh > 0;

  return (
    <div className="card">
      <div className="card-h">
        <span>Цена {symbol}
          {d && chg != null && (
            <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: chg >= 0 ? "var(--ok)" : "var(--bad)" }}>
              {chg >= 0 ? "+" : ""}{chg.toFixed(1)}% за период
            </span>
          )}
          <span style={{ color: "var(--txt-3)", fontWeight: 400, fontSize: 11, marginLeft: 8 }}>закрытия дня · справочно</span>
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          {PRESETS.map(({ p, label }) => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{
                fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                border: "1px solid " + (period === p ? "transparent" : "var(--line)"),
                background: period === p ? "var(--accent)" : "transparent",
                color: period === p ? "#fff" : "var(--txt-2)",
              }}>{label}</button>
          ))}
        </div>
      </div>
      <div className="card-b" style={{ paddingTop: 8 }}>
        {err && <div style={{ fontSize: 12, color: "var(--bad)", padding: "20px 0" }}>Не удалось загрузить цену: {err}</div>}
        {!err && !view && <div style={{ fontSize: 12, color: "var(--txt-3)", padding: "30px 0", textAlign: "center" }}>{loading ? "Загружаю историю цены…" : "Нет данных"}</div>}
        {view && d && (
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
            style={{ width: "100%", height: "auto", display: "block", opacity: loading ? 0.5 : 1, cursor: "crosshair", touchAction: "none" }}
            onPointerMove={onMove} onPointerDown={onClick} onPointerLeave={onLeave}>
            {/* сетка + подписи цены */}
            {view.grid.map((g, i) => (
              <g key={i}>
                <line x1={PAD_L} x2={W - PAD_R} y1={g.yy} y2={g.yy} stroke="var(--line)" strokeDasharray="3 4" strokeWidth="1" />
                <text x={W - PAD_R + 6} y={g.yy + 3.5} fontSize="10" fill="var(--txt-3)">{pFmt(g.v)}</text>
              </g>
            ))}
            {/* BE-коридор: между точками нуля опционы в убытке (лёгкая красная заливка + пунктир) */}
            {hasBE && (
              <g>
                <rect x={PAD_L} width={W - PAD_L - PAD_R} y={view.y(beHigh!)} height={Math.max(0, view.y(beLow!) - view.y(beHigh!))}
                  fill="var(--bad)" opacity="0.045" />
                {[{ v: beHigh!, lbl: `BE ↑ ${pFmt(beHigh!)}` }, { v: beLow!, lbl: `BE ↓ ${pFmt(beLow!)}` }].map((b, i) => (
                  <g key={i}>
                    <line x1={PAD_L} x2={W - PAD_R} y1={view.y(b.v)} y2={view.y(b.v)}
                      stroke="var(--bad)" strokeWidth="1" strokeDasharray="6 4" opacity="0.55" />
                    <text x={PAD_L + 3} y={view.y(b.v) + (i === 0 ? -4 : 11)} fontSize="9.5" fontWeight="600" fill="var(--bad)" opacity="0.8">{b.lbl}</text>
                  </g>
                ))}
              </g>
            )}
            {/* область + линия */}
            <path d={view.area} fill="var(--accent)" opacity="0.07" pointerEvents="none" />
            <path d={view.path} fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinejoin="round" pointerEvents="none" />
            {/* хай / лоу полного ряда */}
            <g pointerEvents="none">
              <circle cx={view.x(d.high.date)} cy={view.y(d.high.price)} r="3" fill="var(--ok)" />
              <text x={Math.min(view.x(d.high.date) + 5, W - PAD_R - 4)} y={Math.max(view.y(d.high.price) - 6, 10)} fontSize="10" fontWeight="600" fill="var(--ok)" textAnchor={view.x(d.high.date) > W - PAD_R - 90 ? "end" : "start"}>
                хай {pFmt(d.high.price)} · {dFmt(d.high.date)}
              </text>
              <circle cx={view.x(d.low.date)} cy={view.y(d.low.price)} r="3" fill="var(--bad)" />
              <text x={Math.min(view.x(d.low.date) + 5, W - PAD_R - 4)} y={Math.min(view.y(d.low.price) + 13, H - PAD_B - 2)} fontSize="10" fontWeight="600" fill="var(--bad)" textAnchor={view.x(d.low.date) > W - PAD_R - 90 ? "end" : "start"}>
                лоу {pFmt(d.low.price)} · {dFmt(d.low.date)}
              </text>
            </g>
            {/* последняя цена */}
            <circle cx={view.x(d.last.date)} cy={view.y(d.last.price)} r="3.2" fill="var(--accent)" pointerEvents="none" />
            <text x={W - PAD_R + 6} y={view.y(d.last.price) + 3.5} fontSize="10.5" fontWeight="700" fill="var(--txt)" pointerEvents="none">{pFmt(d.last.price)}</text>
            {/* подписи дат по краям */}
            <text x={PAD_L} y={H - 6} fontSize="10" fill="var(--txt-3)">{dFmt(view.first, true)}</text>
            <text x={W - PAD_R} y={H - 6} fontSize="10" fill="var(--txt-3)" textAnchor="end">{dFmt(view.last, true)}</text>
            {/* crosshair: вертикаль + точка + плашка дата/цена (клик — закрепить) */}
            {hover && (() => {
              const hx = view.x(hover.date), hy = view.y(hover.price);
              const label = `${pFmt(hover.price)} · ${dFmt(hover.date, true)}`;
              const bw = label.length * 6.2 + 14;
              const bx = Math.min(Math.max(hx - bw / 2, PAD_L), W - PAD_R - bw);
              return (
                <g pointerEvents="none">
                  <line x1={hx} x2={hx} y1={PAD_T - 4} y2={H - PAD_B} stroke="var(--txt-3)" strokeWidth="1" strokeDasharray="2 3" />
                  <circle cx={hx} cy={hy} r="3.5" fill="var(--accent)" stroke="var(--surface)" strokeWidth="1.5" />
                  <rect x={bx} y={1} width={bw} height={16} rx="5" fill="var(--surface-2)" stroke={pinned ? "var(--accent)" : "var(--line-2)"} strokeWidth="1" />
                  <text x={bx + bw / 2} y={12.5} fontSize="10.5" fontWeight="700" fill="var(--txt)" textAnchor="middle">{label}</text>
                </g>
              );
            })()}
          </svg>
        )}
        {view && hasBE && (
          <div style={{ fontSize: 11, color: "var(--txt-3)", marginTop: 6 }}>
            Красный коридор {pFmt(beLow!)} – {pFmt(beHigh!)} — зона, где опционы не окупаются. Смотри, как часто цена выходила за него раньше. Клик по графику — закрепить точку.
          </div>
        )}
      </div>
    </div>
  );
}
